import { useId } from "react";
import type { InputTargetConversation } from "@/api/conversation-targets";
import { Spinner } from "@/components/ui";
import {
  inputTargetsErrorMessage,
  useInputTargets,
} from "@/hooks/use-input-targets";
import { usePickInputTarget } from "@/hooks/use-pick-input-target";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import { InputTargetRowContent } from "./input-target-row-content";

/** Up to five recent Denker chats and sub-agents inside the ring menu. */
export function InputBarRecentTargets({
  currentConversationId,
  onPicked,
}: {
  currentConversationId: string | null;
  onPicked: () => void;
}) {
  const t = useT(inputBarPickersMessages);
  const recent = useInputTargets({ enabled: true, search: "" });
  const { pick, pickingId, error } = usePickInputTarget();
  const headingId = useId();
  return (
    <div className="flex flex-col gap-1" data-testid="input-bar-recent-targets">
      <p
        id={headingId}
        className="px-appkit-xs text-appkit-mini font-medium uppercase tracking-wide text-muted"
      >
        {t("recent")}
      </p>
      {recent.status === "loading" && recent.rows.length === 0 ? (
        <div className="flex justify-center py-1.5">
          <Spinner
            className="h-3.5 w-3.5"
            data-testid="input-bar-recent-targets-loading"
          />
        </div>
      ) : null}
      {recent.status === "error" ? (
        <p role="alert" className="px-appkit-xs text-appkit-mini text-danger">
          {inputTargetsErrorMessage()}
        </p>
      ) : null}
      {recent.status === "ready" && recent.rows.length === 0 ? (
        <p className="px-appkit-xs text-appkit-mini text-muted">
          {t("noRecentTargets")}
        </p>
      ) : null}
      {recent.rows.length > 0 ? (
        <div role="group" aria-labelledby={headingId} className="flex flex-col">
          {recent.rows.map((row) => (
            <RecentTargetRow
              key={row.id}
              row={row}
              current={row.id === currentConversationId}
              disabled={pickingId !== null}
              onPick={async () => {
                if (pickingId !== null) return;
                // Close on success: a cross-project pick refetches and reorders the list.
                if (await pick(row)) onPicked();
              }}
            />
          ))}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="px-appkit-xs text-appkit-mini text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RecentTargetRow({
  row,
  current,
  disabled,
  onPick,
}: {
  row: InputTargetConversation;
  current: boolean;
  disabled: boolean;
  onPick: () => Promise<void>;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={current}
      // aria-disabled keeps keyboard focus (and Escape) inside the menu during a pick.
      aria-disabled={disabled || undefined}
      onClick={() => void onPick()}
      data-testid="input-bar-recent-target"
      data-target-kind={row.target_kind}
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-[16px] px-2 py-1.5 text-left transition-colors aria-disabled:cursor-wait",
        current
          ? "bg-[var(--color-surface-selected)]"
          : "hover:bg-[var(--color-interactive-hover)]",
      )}
    >
      <InputTargetRowContent row={row} current={current} />
    </button>
  );
}
